// Service Steady PWA — src/lib/weatherStyle.ts — v3.0 — built 2026-06-04
//
// Gradient class lookup for the weather card. Server returns
// current.category (one of 7 buckets) + current.is_day. We map that pair to
// a Tailwind gradient string the card uses as its bg.
//
// Keys mirror weatherCodeToCategory() in Voice AI/Worker/src/handlers/
// weatherCodes.ts. Keep both in sync.

export type WeatherCategory =
  | 'clear'
  | 'partly_cloudy'
  | 'cloudy'
  | 'rain'
  | 'snow'
  | 'thunder'
  | 'fog';

const DAY_GRADIENTS: Record<WeatherCategory, string> = {
  clear: 'from-sky-400 via-cyan-400 to-blue-500',
  partly_cloudy: 'from-sky-300 via-slate-300 to-slate-400',
  cloudy: 'from-slate-400 via-slate-500 to-slate-600',
  rain: 'from-blue-500 via-indigo-600 to-slate-700',
  snow: 'from-sky-200 via-blue-100 to-slate-300',
  thunder: 'from-slate-700 via-slate-900 to-black',
  fog: 'from-slate-300 via-slate-400 to-slate-500',
};

const NIGHT_GRADIENTS: Record<WeatherCategory, string> = {
  clear: 'from-indigo-900 via-purple-900 to-slate-900',
  partly_cloudy: 'from-slate-700 via-slate-800 to-indigo-900',
  cloudy: 'from-slate-600 via-slate-700 to-slate-900',
  rain: 'from-slate-700 via-indigo-900 to-slate-900',
  snow: 'from-slate-600 via-slate-700 to-slate-900',
  thunder: 'from-slate-700 via-slate-900 to-black',
  fog: 'from-slate-500 via-slate-600 to-slate-800',
};

export function getGradient(category: WeatherCategory, isDay: boolean): string {
  return isDay ? DAY_GRADIENTS[category] : NIGHT_GRADIENTS[category];
}

/** Text color over a gradient. Snow-day is the only legible-light bg; the
 *  rest use white text. */
export function getTextOnGradient(
  category: WeatherCategory,
  isDay: boolean,
): 'text-white' | 'text-slate-900' {
  if (isDay && category === 'snow') return 'text-slate-900';
  return 'text-white';
}

/** AQI badge color classes — surfaced in the detail modal. */
export function aqiBadgeClasses(
  category:
    | 'good'
    | 'moderate'
    | 'usg'
    | 'unhealthy'
    | 'very_unhealthy'
    | 'hazardous'
    | 'unknown',
): string {
  switch (category) {
    case 'good':
      return 'bg-emerald-500 text-white';
    case 'moderate':
      return 'bg-yellow-400 text-yellow-950';
    case 'usg':
      return 'bg-orange-500 text-white';
    case 'unhealthy':
      return 'bg-red-600 text-white';
    case 'very_unhealthy':
      return 'bg-purple-700 text-white';
    case 'hazardous':
      return 'bg-rose-900 text-white';
    case 'unknown':
    default:
      return 'bg-slate-400 text-white';
  }
}

export function aqiLabel(
  category:
    | 'good'
    | 'moderate'
    | 'usg'
    | 'unhealthy'
    | 'very_unhealthy'
    | 'hazardous'
    | 'unknown',
): string {
  switch (category) {
    case 'good':
      return 'Good';
    case 'moderate':
      return 'Moderate';
    case 'usg':
      return 'USG';
    case 'unhealthy':
      return 'Unhealthy';
    case 'very_unhealthy':
      return 'Very Unhealthy';
    case 'hazardous':
      return 'Hazardous';
    case 'unknown':
    default:
      return 'Unknown';
  }
}
